import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { saveWorkflowRequest } from "../../../store/slices/workflow";
import { makeWorkflowId, mapFlowToWorkflowDefinition, serializeWorkflowForBackend } from "../utils/workflowFlowMapper";
import { buildWorkflowCanvasFingerprint } from "../utils/workflowDirtyFingerprint";

const readSavedWorkflowId = (saveResponse) =>
  saveResponse?.workflowId || saveResponse?.workflow?.workflowId || null;

const isNewWorkflowRouteId = (routeId) => routeId === "new" || routeId === "new-flow-ai";

export function useWorkflowUnsavedGuard({
  routeId,
  nodes,
  edges,
  name,
  workflowStart,
  sessionTimeoutSeconds,
  globalTransitions,
  intentName = "",
  exampleUtterances = [""],
  formatIntentName,
  pendingSaveWorkflowIdRef,
  workflowSave,
  dispatch,
  hydrationReady,
  onNotify,
  flushPendingMedia,
  nodesRef,
  edgesRef,
  workflowStartRef,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const mediaUploadLoading = useSelector((state) => state.workflowSave?.mediaUpload?.loading ?? false);
  const workflowDetail = useSelector((state) => state.workflowDetail?.workflow);

  const fingerprint = useMemo(
    () => buildWorkflowCanvasFingerprint({
      nodes,
      edges,
      name,
      workflowStart,
      sessionTimeoutSeconds,
      globalTransitions,
      intentName,
      exampleUtterances,
      formatIntentName,
    }),
    [nodes, edges, name, workflowStart, sessionTimeoutSeconds, globalTransitions, intentName, exampleUtterances, formatIntentName]
  );

  const baselineFingerprintRef = useRef(null);
  const pendingLeaveActionRef = useRef(null);
  const pendingNavigationRef = useRef(null);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  const isDirty = hydrationReady
    && baselineFingerprintRef.current != null
    && fingerprint !== baselineFingerprintRef.current;

  const markBaseline = useCallback((nextFingerprint = fingerprint) => {
    baselineFingerprintRef.current = nextFingerprint;
  }, [fingerprint]);

  const saveNow = useCallback(async ({ silent = false, onComplete } = {}) => {
    const workflowId = isNewWorkflowRouteId(routeId) ? makeWorkflowId(name) : routeId;

    if (!workflowId || !name?.trim()) {
      if (!silent) {
        onNotify?.("Workflow name is required.", "error");
      }
      return { ok: false, reason: "name_required" };
    }

    if (flushPendingMedia) {
      const flushResult = await flushPendingMedia({ silent });
      if (!flushResult?.ok) {
        if (!silent) {
          onNotify?.("Failed to upload media. Check the image and try again.", "error");
        }
        return { ok: false, reason: "media_failed" };
      }

      if (flushResult.nodes) {
        nodesRef.current = flushResult.nodes;
      }
    } else if (mediaUploadLoading) {
      return { ok: false, reason: "pending_media" };
    }

    const nodesToSave = nodesRef?.current ?? nodes;
    const edgesToSave = edgesRef?.current ?? edges;
    const startToSave = workflowStartRef?.current ?? workflowStart;

    const definition = mapFlowToWorkflowDefinition(nodesToSave, edgesToSave, {
      start: startToSave,
      sessionTimeoutSeconds,
      globalTransitions,
    });

    const finalDefinition = { ...definition };
    const cleanIntent = formatIntentName?.(intentName) ?? "";
    if (cleanIntent) {
      finalDefinition.intent = cleanIntent;
      finalDefinition.routing = {
        description: workflowDetail?.definition?.routing?.description ?? "",
        examples: (exampleUtterances ?? []).map((utterance) => utterance.trim()).filter(Boolean),
      };
    } else {
      if (workflowDetail?.definition?.routing) {
        finalDefinition.routing = {
          description: workflowDetail.definition.routing.description ?? "",
          examples: (workflowDetail.definition.routing.examples ?? []).map((u) => u.trim()).filter(Boolean),
        };
      }
    }

    pendingSaveWorkflowIdRef.current = workflowId;
    pendingLeaveActionRef.current = onComplete || null;

    const serializedPayload = serializeWorkflowForBackend({
      workflowId,
      name: name.trim() || workflowId,
      definition: finalDefinition,
    });

    dispatch(saveWorkflowRequest({
      operation: "SAVE_WORKFLOW",
      payload: serializedPayload,
      meta: { silent },
    }));

    return { ok: true };
  }, [
    dispatch,
    edges,
    edgesRef,
    exampleUtterances,
    flushPendingMedia,
    formatIntentName,
    globalTransitions,
    intentName,
    mediaUploadLoading,
    name,
    nodes,
    nodesRef,
    onNotify,
    pendingSaveWorkflowIdRef,
    routeId,
    sessionTimeoutSeconds,
    workflowStart,
    workflowStartRef,
    workflowDetail,
  ]);

  useEffect(() => {
    baselineFingerprintRef.current = null;
  }, [routeId]);

  useEffect(() => {
    if (!hydrationReady) return;

    if (baselineFingerprintRef.current == null) {
      markBaseline(fingerprint);
    }
  }, [fingerprint, hydrationReady, markBaseline]);

  useEffect(() => {
    if (workflowSave.loading || workflowSave.error || !workflowSave.response) return;

    const persistedId = readSavedWorkflowId(workflowSave.response) || pendingSaveWorkflowIdRef.current;
    if (!persistedId) return;

    if (!isNewWorkflowRouteId(routeId) && persistedId !== routeId) return;

    markBaseline(fingerprint);
    pendingLeaveActionRef.current?.();
    pendingLeaveActionRef.current = null;
  }, [
    fingerprint,
    markBaseline,
    pendingSaveWorkflowIdRef,
    routeId,
    workflowSave.error,
    workflowSave.loading,
    workflowSave.response,
  ]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const openLeaveDialog = useCallback((onProceed) => {
    pendingNavigationRef.current = onProceed || null;
    setShowLeaveDialog(true);
  }, []);

  const confirmLeave = useCallback((onProceed) => {
    if (!isDirty) {
      onProceed?.();
      return;
    }
    openLeaveDialog(onProceed);
  }, [isDirty, openLeaveDialog]);

  const confirmLeaveRef = useRef(confirmLeave);
  const isDirtyRef = useRef(isDirty);
  confirmLeaveRef.current = confirmLeave;
  isDirtyRef.current = isDirty;

  /** BrowserRouter (RR7) has no navigator.block — intercept sidebar/header <Link> clicks. */
  useEffect(() => {
    if (!isDirty) return undefined;

    const handleDocumentClick = (event) => {
      if (!isDirtyRef.current) return;

      const anchor = event.target?.closest?.("a[href]");
      if (!anchor || anchor.hasAttribute("download")) return;
      if (anchor.target === "_blank") return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const rawHref = anchor.getAttribute("href");
      if (!rawHref || rawHref.startsWith("#") || rawHref.startsWith("mailto:") || rawHref.startsWith("tel:")) {
        return;
      }

      let targetUrl;
      try {
        targetUrl = new URL(rawHref, window.location.origin);
      } catch {
        return;
      }

      if (targetUrl.origin !== window.location.origin) return;

      const targetPath = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      if (targetPath === currentPath) return;

      event.preventDefault();
      event.stopPropagation();

      confirmLeaveRef.current?.(() => {
        navigate(targetPath);
      });
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () => document.removeEventListener("click", handleDocumentClick, true);
  }, [isDirty, location.hash, location.pathname, location.search, navigate]);

  const completePendingNavigation = useCallback(() => {
    const proceed = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    proceed?.();
  }, []);

  const handleLeaveCancel = useCallback(() => {
    setShowLeaveDialog(false);
    pendingLeaveActionRef.current = null;
    pendingNavigationRef.current = null;
  }, []);

  const handleLeaveWithoutSaving = useCallback(() => {
    markBaseline(fingerprint);
    setShowLeaveDialog(false);
    completePendingNavigation();
  }, [completePendingNavigation, fingerprint, markBaseline]);

  const handleSaveAndLeave = useCallback(() => {
    void saveNow({
      silent: true,
      onComplete: () => {
        setShowLeaveDialog(false);
        completePendingNavigation();
      },
    }).then((result) => {
      if (result?.reason === "name_required") {
        onNotify?.("Workflow name is required before saving.", "error");
        handleLeaveCancel();
      } else if (result?.reason === "media_failed") {
        handleLeaveCancel();
      }
    });
  }, [completePendingNavigation, handleLeaveCancel, onNotify, saveNow]);

  return {
    saveNow,
    confirmLeave,
    leaveDialogProps: {
      open: showLeaveDialog,
      onOpenChange: (open) => {
        if (!open) handleLeaveCancel();
      },
      isSaving: workflowSave.loading || mediaUploadLoading,
      onSave: handleSaveAndLeave,
      onDontSave: handleLeaveWithoutSaving,
    },
  };
}
